import React, { useEffect } from "react";
import { Box, Button, CheckBox, Table, TableBody, TableCell, TableHeader, TableRow, Text } from "grommet";
import { CalendarAccessPolicy, CalendarViewModel, GetCalendarAccessPolicies, UpdateCalendarAccessPolicy } from "./CalendarStore";

import { useNavigate, useParams } from "react-router-dom";
import AccessRuleRenderer, { IAccessLevel } from "../components/AccessRuleRenderer";
import { orderBy, mapValues, keyBy, set, create } from "lodash";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IdentityRuleSelectorSearch } from "../components/IdentityRuleSelector";

export const ACCESSLEVELS = [
    { level: 1, name: "View" },
    { level: 2, name: "Edit" },
    { level: 4, name: "Delete" },
    { level: 8, name: "Create Event" },
];

export function accessTypeToAccessLevels(accessType: number): IAccessLevel[] {
    let levels = [] as IAccessLevel[];
    orderBy(ACCESSLEVELS, "level", "desc").forEach((accessLevel) => {
        if (accessType >= accessLevel.level) {
            accessType -= accessLevel.level;
            levels.push(accessLevel);
        }
    });
    return levels;
}

export function accessTypeValueToAccessLevelState(accessTypeValue) {
    return mapValues(keyBy(accessTypeToAccessLevels(accessTypeValue), "level"), () => true);
}

export const CalendarAccessTypeSelector = ({ handleChange, value, readonly }: { handleChange?: any; value?: any; readonly?: boolean }) => {
    const navigate = useNavigate();
    const params = useParams();

    const [accessLevels, setAccessLevels] = React.useState(value);

    React.useEffect(() => {
        setAccessLevels(value);
    }, [value]);

    const handleAccessLevelChange = (level) => (e) => {
        let value = e.target.checked;
        let _accessLevels = { ...accessLevels };

        if (value) {
            //add
            if (_accessLevels[level] === undefined) {
                _accessLevels[level] = true;
            }
        } else {
            //remove
            if (_accessLevels[level] !== undefined) {
                delete _accessLevels[level];
            }
        }

        setAccessLevels(_accessLevels);
        handleChange(_accessLevels);
    };

    return (
        <Box>
            {ACCESSLEVELS.map((accessLevel, key) => (
                <Box key={key} pad={{ vertical: "xsmall" }} direction="row" gap="small">
                    <CheckBox key={key} checked={!!accessLevels[accessLevel.level]} onChange={handleAccessLevelChange(accessLevel.level)} disabled={readonly}></CheckBox>
                    {accessLevel.name}
                </Box>
            ))}
        </Box>
    );
};

interface CalendarAccessPoliciesProps {
    calendar: CalendarViewModel;
}

export const CalendarAccessPolicies = ({ calendar }: CalendarAccessPoliciesProps) => {
    const [accessPolicies, setAccessPolicies] = React.useState<CalendarAccessPolicy[]>([]);

    const removeAccessPolicy = (rule) => () => {
        UpdateCalendarAccessPolicy({ calendarInstanceID: calendar.id, rule, accessType: 0 }).then(() => {
            fetchAccessPolicies();
        });
    };

    useEffect(() => {
        fetchAccessPolicies();
    }, [calendar.id]);

    const fetchAccessPolicies = () => {
        GetCalendarAccessPolicies(calendar.id)
            .then((response) => {
                setAccessPolicies(response?.data?.result || []);
            })
            .catch((error) => {
                setAccessPolicies([]);
            });
    };

    const [mode, setMode] = React.useState("view" as "view" | "edit");

    const [selectedAccessPolicy, setSelectedAccessPolicy] = React.useState<CalendarAccessPolicy | null>(null);

    const handleAccessTypeChange = (accessType) => {
        setSelectedAccessPolicy({
            ...selectedAccessPolicy,
            accessType: Object.keys(accessType).reduce((acc, key) => {
                if (accessType[key]) {
                    return acc + parseInt(key);
                }
                return acc;
            }, 0),
        } as CalendarAccessPolicy);
    };

    const createAccessPolicy = () => {
        setSelectedAccessPolicy({
            calendarInstanceID: calendar.id,
            rule: "",
            accessType: 0,
        } as CalendarAccessPolicy);
        setMode("edit");
    };

    const saveAccessPolicy = () => {
        if (selectedAccessPolicy?.rule) {
            UpdateCalendarAccessPolicy(selectedAccessPolicy).then(() => {
                setMode("view");
                fetchAccessPolicies();
            });
        } else {
            alert("Please select an access rule.");
        }
    };

    return (
        <Box>
            {mode == "view" && (
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableCell>Access Rule</TableCell>
                            <TableCell>Permissions</TableCell>
                            <TableCell></TableCell>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {accessPolicies.map((accessPolicy, key) => (
                            <TableRow key={key}>
                                <TableCell>
                                    <AccessRuleRenderer rule={accessPolicy.rule}></AccessRuleRenderer>
                                </TableCell>
                                <TableCell>
                                    <CalendarAccessTypeSelector value={accessTypeValueToAccessLevelState(accessPolicy.accessType)} readonly></CalendarAccessTypeSelector>
                                </TableCell>
                                <TableCell>
                                    <Box direction="row" gap="small" align="center">
                                        <Button
                                            label="Edit"
                                            onClick={() => {
                                                setSelectedAccessPolicy(accessPolicy);
                                                setMode("edit");
                                            }}
                                        />
                                        <Button icon={<FontAwesomeIcon icon={["fad", "trash"]}></FontAwesomeIcon>} onClick={removeAccessPolicy(accessPolicy.rule)}></Button>
                                    </Box>
                                </TableCell>
                            </TableRow>
                        ))}
                        <TableRow>
                            <TableCell>
                                <Button label="Create New Access Rule" onClick={() => createAccessPolicy()} />
                            </TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            )}
            {mode == "edit" && (
                <Box>
                    <Box direction="row" gap={"small"} align="center">
                        {!selectedAccessPolicy?.rule && <span>No Access Rule Selected</span>}
                        <AccessRuleRenderer rule={selectedAccessPolicy?.rule || ""} />
                        <IdentityRuleSelectorSearch
                            onChange={(rule) => {
                                setSelectedAccessPolicy({ ...selectedAccessPolicy, rule } as CalendarAccessPolicy);
                            }}
                        />
                    </Box>
                    <CalendarAccessTypeSelector value={accessTypeValueToAccessLevelState(selectedAccessPolicy?.accessType)} handleChange={handleAccessTypeChange} />
                    <Button label="Save" onClick={saveAccessPolicy} />
                </Box>
            )}
        </Box>
    );
};
